/**
 * Injizierbare Uhr. `now()` liefert Sekunden auf einer monotonen Zeitbasis.
 * Siehe SCHEDULER.md fuer das Timing-Modell.
 */
export interface Clock {
  now(): number;
}

export class RealClock implements Clock {
  private readonly reference: () => number;

  constructor(reference: () => number = () => performance.now() / 1000) {
    this.reference = reference;
  }

  now(): number {
    return this.reference();
  }
}

/** Virtuelle Uhr fuer deterministische Tests. Zeit vergeht nur bei explizitem advance(). */
export class VirtualClock implements Clock {
  private time: number;

  constructor(startTime = 0) {
    this.time = startTime;
  }

  now(): number {
    return this.time;
  }

  advance(seconds: number): void {
    if (seconds < 0) {
      throw new Error('VirtualClock.advance erwartet einen nicht-negativen Betrag.');
    }
    this.time += seconds;
  }

  set(seconds: number): void {
    this.time = seconds;
  }
}

/** Treibt den Scheduler-Tick an. In Tests manuell, im Browser per Intervall. */
export interface TimerDriver {
  start(callback: () => void, intervalSeconds: number): void;
  stop(): void;
}

export class IntervalTimerDriver implements TimerDriver {
  private handle: ReturnType<typeof setInterval> | undefined;

  start(callback: () => void, intervalSeconds: number): void {
    this.stop();
    this.handle = setInterval(callback, intervalSeconds * 1000);
  }

  stop(): void {
    if (this.handle !== undefined) {
      clearInterval(this.handle);
      this.handle = undefined;
    }
  }
}

/** Manuelle Treiber-Attrappe fuer Tests: kein echter Timer, Ticks werden explizit ausgeloest. */
export class ManualTimerDriver implements TimerDriver {
  private callback: (() => void) | undefined;
  private running = false;

  start(callback: () => void): void {
    this.callback = callback;
    this.running = true;
  }

  stop(): void {
    this.running = false;
    this.callback = undefined;
  }

  get isRunning(): boolean {
    return this.running;
  }

  tick(): void {
    this.callback?.();
  }
}
